import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-project-todo',
  templateUrl: './project-todo.component.html',
  styleUrls: ['./project-todo.component.scss']
})
export class ProjectTodoComponent implements OnInit {
  loading = false;
  tasks: any[] = [];
  status = 'all';
  commentDrafts: Record<number, string> = {};
  replyTargets: Record<number, number | null> = {};
  commentAttachments: Record<number, any[]> = {};
  staffOptions: any[] = [];
  previewMedia: any = null;
  mentionState = {
    taskId: null as number | null,
    visible: false,
    query: '',
    start: 0,
    end: 0
  };
  postingCommentFor: number | null = null;

  constructor(private pm: ProjectManagementService) {}

  ngOnInit(): void {
    this.pm.getStaffs().subscribe({ next: (rows) => this.staffOptions = rows || [], error: () => this.staffOptions = [] });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.pm.getMyTasks({ status: this.status }).subscribe({
      next: (res) => { this.tasks = res || []; this.loading = false; },
      error: () => { this.tasks = []; this.loading = false; }
    });
  }

  progressWidth(t: any): number {
    return Math.min(100, Math.max(0, Number(t.progressPercent || 0)));
  }

  statusClass(t: any): string {
    const slug = String(t?.status || '').toLowerCase().replace(/\\s+/g, '-');
    return `status-${slug}`;
  }

  addComment(task: any): void {
    const taskId = Number(task?.id || 0);
    const comment = (this.commentDrafts[taskId] || '').trim();
    if (!taskId) return;
    if (!comment) {
      Swal.fire('Validation', 'Enter an update before posting.', 'warning');
      return;
    }

    this.postingCommentFor = taskId;
    this.pm.addTaskComment(taskId, {
      comment,
      parentCommentId: this.replyTargets[taskId] || null,
      mediaAttachments: this.commentAttachments[taskId] || [],
      mentionedStaffIds: this.extractMentionedStaffIds(this.commentDrafts[taskId] || '')
    }).subscribe({
      next: () => {
        this.commentDrafts[taskId] = '';
        this.replyTargets[taskId] = null;
        this.commentAttachments[taskId] = [];
        this.postingCommentFor = null;
        this.load();
      },
      error: (err) => {
        this.postingCommentFor = null;
        Swal.fire('Error', err?.error?.message || 'Failed to post task update.', 'error');
      }
    });
  }

  setReplyTarget(taskId: number, commentId: number | null): void {
    this.replyTargets[taskId] = commentId;
  }

  onCommentInput(taskId: number, event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.updateMentionState(taskId, textarea.value, textarea.selectionStart || 0);
  }

  insertCommentMention(taskId: number, staff: any, textarea: HTMLTextAreaElement): void {
    const value = String(this.commentDrafts[taskId] || '');
    const before = value.slice(0, this.mentionState.start);
    const after = value.slice(this.mentionState.end);
    this.commentDrafts[taskId] = `${before}@${staff.fullName} ${after}`.replace(/\s{2,}/g, ' ');
    this.mentionState.visible = false;
    textarea.focus();
  }

  mentionSuggestions(taskId: number): any[] {
    if (!this.mentionState.visible || this.mentionState.taskId !== taskId) return [];
    return this.staffOptions
      .filter((staff) => !this.mentionState.query || String(staff.fullName || '').toLowerCase().includes(this.mentionState.query))
      .slice(0, 6);
  }

  mediaKind(url: string, contentType?: string): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
    const type = String(contentType || '').toLowerCase();
    const source = String(url || '').toLowerCase();
    if (type.startsWith('image/') || source.startsWith('data:image/')) return 'image';
    if (type.startsWith('video/') || source.startsWith('data:video/')) return 'video';
    if (type.startsWith('audio/') || source.startsWith('data:audio/')) return 'audio';
    if (type === 'application/pdf' || source.startsWith('data:application/pdf')) return 'pdf';
    return 'file';
  }

  openPreview(media: any): void {
    this.previewMedia = media || null;
  }

  closePreview(): void {
    this.previewMedia = null;
  }

  async onCommentMediaSelected(taskId: number, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    await this.appendCommentMedia(taskId, files);
    input.value = '';
  }

  async onCommentMediaDrop(taskId: number, event: DragEvent): Promise<void> {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    await this.appendCommentMedia(taskId, files);
  }

  private async appendCommentMedia(taskId: number, files: File[]): Promise<void> {
    try {
      const attachments = await Promise.all(files.map((file) => this.readFile(file)));
      this.commentAttachments[taskId] = [...(this.commentAttachments[taskId] || []), ...attachments];
    } catch {
      Swal.fire('Error', 'Failed to read one or more files.', 'error');
    }
  }

  removeCommentMedia(taskId: number, index: number): void {
    this.commentAttachments[taskId] = (this.commentAttachments[taskId] || []).filter((_: any, i: number) => i !== index);
  }

  private readFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        fileName: file.name,
        url: reader.result as string,
        contentType: file.type || 'application/octet-stream'
      });
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private updateMentionState(taskId: number, text: string, caret: number): void {
    const value = String(text || '');
    const left = value.slice(0, caret);
    const match = left.match(/(^|\s)@([^\s@]*)$/);
    if (!match) {
      this.mentionState = { taskId: null, visible: false, query: '', start: 0, end: 0 };
      return;
    }

    this.mentionState = {
      taskId,
      visible: true,
      query: (match[2] || '').toLowerCase(),
      start: caret - match[2].length - 1,
      end: caret
    };
  }

  private extractMentionedStaffIds(text: string): number[] {
    const value = String(text || '');
    return this.staffOptions
      .filter((staff) => value.includes(`@${staff.fullName}`))
      .map((staff) => staff.id);
  }
}
